/* global window */

import React, { useEffect, useRef, useState } from 'react';
import RxDotFilled from '../icons/RxDotFilled';
import RxDot from '../icons/RxDot';
import BsChevronRight from '../icons/BsChevronRight';
import BsChevronLeft from '../icons/BsChevronLeft';

import '../styles/HeroSlideshow.css';
import HeroSlideshowItem from './HeroSlideshowItem';
import { array, bool } from 'prop-types';
import useIsRendered from '../hooks/useIsRendered';

export default function HeroSlideshow({ isMobile, items = [] }) {
    const slideShowRef = useRef(null);
    const [selected, setSelected] = useState(0);
    const [isSwitching, setIsSwitching] = useState(false);
    const [startX, setStartX] = useState(0);
    const [offsetXPercent, setOffsetXPercent] = useState(0);
    const isRendered = useIsRendered();

    useEffect(() => {
        /* Auto switch */
        let autoSwitchTimer = null;
        const AUTO_SWITCH_INTERVAL_MS = 7500;
        const autoSwitchFunc = () => {
            setSelected((selected + 1) < items.length ? (selected + 1) : 0);
        };
        if (!isSwitching && items.length) {
            autoSwitchTimer = setTimeout(autoSwitchFunc, AUTO_SWITCH_INTERVAL_MS);
        }
        /* Manual switch */
        setOffsetXPercent(0);
        const handleMouseMove = (e) => {
            if (isSwitching === true) {
                const newX = e.pageX ?? e.touches[0].clientX;
                const slideShowWidth = Math.floor(slideShowRef.current.offsetWidth / items.length);
                // Check difference
                const diffX = (newX - startX);
                const newOffsetXPercent = Math.floor(diffX / slideShowWidth * 100);
                const SWITCH_DIFF_PERCENT = 15;
                if (newOffsetXPercent > SWITCH_DIFF_PERCENT) {
                    setSelected((selected - 1) >= 0 ? (selected - 1) : (items.length - 1));
                    setIsSwitching(false);
                } else if (newOffsetXPercent <= -SWITCH_DIFF_PERCENT) {
                    setSelected((selected + 1) < items.length ? (selected + 1) : 0);
                    setIsSwitching(false);
                } else {
                    setOffsetXPercent(newOffsetXPercent * 2.04);
                }
            }
        };
        if (isRendered) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('touchmove', handleMouseMove);
        }
        return () => {
            clearTimeout(autoSwitchTimer);
            if (isRendered) {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('touchmove', handleMouseMove);
            }
        };
    }, [selected, isSwitching, items]);
    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsSwitching(true);
        setOffsetXPercent(0);
        setStartX(e.pageX ?? e.touches[0].clientX);
    };
    const handleMouseUp = (e) => {
        setIsSwitching(false);
    };
    return (<>
        { items.length > 0 && (<div
            className={'hero-slideshow__container'}>
            <div
                ref={slideShowRef}
                className={'hero-slideshow__container--inner'}
                style={{
                    transform: `translateX(${Math.floor(-(selected * 100) + offsetXPercent) / 100 * (window?.innerWidth ?? 0)}px)`,
                    transitionDelay: '',
                    width: `${items.length * 100}%`,
                }}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchEnd={handleMouseUp}>
                {items.map((item, idx) => (<HeroSlideshowItem
                    key={idx}
                    backgroundUrl={item.backgroundUrl}
                    headingText={item.headingText}
                    paragraphText={item.paragraphText}
                    buttonText={item.buttonText}
                    buttonUrl={item.buttonUrl}
                    itemWidth={100 / items.length} />))}
            </div>
            <div
                className={'hero-slideshow__controls arrows'}>
                <button
                    className={'hero-slideshow__controls--btn'}
                    onClick={() => setSelected((selected - 1) >= 0 ? (selected - 1) : (items.length - 1))}><BsChevronLeft
                        size={'25px'} /></button>
                <button
                    className={'hero-slideshow__controls--btn'}
                    onClick={() => setSelected((selected + 1) < items.length ? (selected + 1) : 0)}><BsChevronRight
                        size={'25px'} /></button>
            </div>
            <div
                className={'hero-slideshow__controls'}>
                {items.map((item, idx) => (<button
                    key={idx}
                    className={'hero-slideshow__controls--btn'}
                    onClick={() => setSelected(idx)}>
                    {selected === idx && (<RxDotFilled
                        size={'25px'} />)}
                    {selected !== idx && (<RxDot
                        size={'25px'} />)}
                </button>))}
            </div>
        </div>)}
    </>);
}

HeroSlideshow.propTypes = {
    isMobile: bool,
    items: array,
};